import { createContext, useState } from "react";
import {
  apiRequest,
  apiRequestFormWithToken,
  apiRequestWithToken
} from "../api/client";

export const AuthContext = createContext();

const STORAGE_KEYS = {
  userSession: "auth_user_session_v2",
  adminSession: "auth_admin_session_v2",
  userMeta: "auth_user_meta_v2"
};

const safeJsonParse = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const normalizeEmail = (value) => value.trim().toLowerCase();

const sessionFromApiUser = (apiUser, token, meta = {}) => ({
  id: apiUser._id,
  name: apiUser.name,
  email: apiUser.email,
  isAdmin: Boolean(apiUser.isAdmin),
  phone: meta.phone || "",
  address: meta.address || "Working Address",
  avatar: meta.avatar || "",
  token
});

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(() =>
    safeJsonParse(localStorage.getItem(STORAGE_KEYS.userSession), null)
  );
  const [admin, setAdmin] = useState(() =>
    safeJsonParse(localStorage.getItem(STORAGE_KEYS.adminSession), null)
  );
  const [userMeta, setUserMeta] = useState(() =>
    safeJsonParse(localStorage.getItem(STORAGE_KEYS.userMeta), {})
  );

  const persistMeta = (nextMeta) => {
    setUserMeta(nextMeta);
    localStorage.setItem(STORAGE_KEYS.userMeta, JSON.stringify(nextMeta));
  };

  const enrichSessionWithMeta = (session, fallbackMeta = {}) => {
    const key = normalizeEmail(session.email);
    const metaForUser = userMeta[key] || fallbackMeta || {};
    return {
      ...session,
      phone: metaForUser.phone || session.phone || "",
      address: metaForUser.address || session.address || "Working Address",
      avatar: metaForUser.avatar || session.avatar || ""
    };
  };

  const getLoginOptions = async (email) => {
    try {
      const payload = await apiRequest("/api/users/login-options", {
        method: "POST",
        body: JSON.stringify({ email: normalizeEmail(email) })
      });

      return {
        ok: true,
        email: payload.email,
        existingUser: Boolean(payload.existingUser),
        requiresEmailVerification: Boolean(payload.requiresEmailVerification)
      };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const sendLoginVerificationLink = async (email) => {
    try {
      const payload = await apiRequest("/api/users/send-login-verification-link", {
        method: "POST",
        body: JSON.stringify({ email: normalizeEmail(email) })
      });
      return { ok: true, message: payload.message || "Verification link sent." };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const verifyLoginEmailToken = async (token) => {
    try {
      const payload = await apiRequest(
        `/api/users/verify-login-email?token=${encodeURIComponent(token)}`,
        {
          method: "GET"
        }
      );
      return {
        ok: true,
        message: payload.message || "Your gmail is verified.",
        email: payload.email || ""
      };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const requestLoginOtp = async ({ email, name }) => {
    try {
      const payload = await apiRequest("/api/users/request-login-otp", {
        method: "POST",
        body: JSON.stringify({
          email: normalizeEmail(email),
          name: name ? name.trim() : ""
        })
      });

      return { ok: true, message: payload.message || "OTP sent to your email." };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const loginWithOtp = async ({ email, otp, name }) => {
    try {
      const normalizedEmail = normalizeEmail(email);
      const trimmedName = String(name || "").trim();
      const payload = await apiRequest("/api/users/login-with-otp", {
        method: "POST",
        body: JSON.stringify({
          email: normalizedEmail,
          otp: otp.trim(),
          name: trimmedName
        })
      });

      const session = enrichSessionWithMeta(
        {
          id: payload._id,
          name: payload.name,
          email: payload.email,
          isAdmin: Boolean(payload.isAdmin),
          token: payload.token
        },
        userMeta[normalizedEmail]
      );

      setUser(session);
      localStorage.setItem(STORAGE_KEYS.userSession, JSON.stringify(session));
      return { ok: true, user: session };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const login = async ({ email, otp, name }) => loginWithOtp({ email, otp, name });

  const registerUser = async ({ name, email, password }) => {
    try {
      const payload = await apiRequest("/api/users/register", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), email: normalizeEmail(email), password })
      });

      return { ok: true, message: payload.message || "OTP sent to your email." };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const verifySignupOtp = async ({ email, otp }) => {
    try {
      const normalizedEmail = normalizeEmail(email);
      const payload = await apiRequest("/api/users/verify-otp", {
        method: "POST",
        body: JSON.stringify({ email: normalizedEmail, otp: otp.trim() })
      });

      const profile = await apiRequestWithToken("/api/users/profile", payload.token);
      const nextMeta = {
        ...userMeta,
        [normalizedEmail]: {
          phone: userMeta[normalizedEmail]?.phone || "",
          address: userMeta[normalizedEmail]?.address || "Working Address",
          avatar: userMeta[normalizedEmail]?.avatar || ""
        }
      };
      persistMeta(nextMeta);

      const session = sessionFromApiUser(profile, payload.token, nextMeta[normalizedEmail]);
      setUser(session);
      localStorage.setItem(STORAGE_KEYS.userSession, JSON.stringify(session));

      return { ok: true, user: session };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const resendSignupOtp = async (email) => {
    try {
      const payload = await apiRequest("/api/users/resend-otp", {
        method: "POST",
        body: JSON.stringify({ email: normalizeEmail(email) })
      });
      return { ok: true, message: payload.message || "OTP resent successfully." };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const adminLogin = async ({ email }) => {
    try {
      const payload = await apiRequest("/api/users/admin-login", {
        method: "POST",
        body: JSON.stringify({ email: normalizeEmail(email) })
      });

      if (!payload.isAdmin) {
        return { ok: false, error: "Admin access only." };
      }

      const adminSession = {
        id: payload._id,
        email: payload.email,
        name: payload.name,
        token: payload.token,
        role: "admin"
      };

      setAdmin(adminSession);
      localStorage.setItem(STORAGE_KEYS.adminSession, JSON.stringify(adminSession));
      return { ok: true, admin: adminSession };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const requestPasswordResetOtp = async (email) => {
    try {
      const payload = await apiRequest("/api/users/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: normalizeEmail(email) })
      });
      return { ok: true, message: payload.message || "OTP sent for password reset." };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const resetPassword = async ({ email, otp, newPassword }) => {
    try {
      await apiRequest("/api/users/reset-password", {
        method: "POST",
        body: JSON.stringify({
          email: normalizeEmail(email),
          otp: otp.trim(),
          password: newPassword
        })
      });
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const fetchAdminSubmissions = async () => {
    if (!admin?.token) {
      return { ok: false, error: "Admin session expired. Please login again." };
    }

    try {
      const submissions = await apiRequestWithToken(
        "/api/submissions/all",
        admin.token
      );
      return { ok: true, data: submissions };
    } catch (error) {
      if (
        error.message.includes("Session expired") ||
        error.message.includes("token failed") ||
        error.message.includes("No token provided")
      ) {
        adminLogout();
      }
      return { ok: false, error: error.message };
    }
  };

  const fetchMySubmissions = async () => {
    if (!user?.token) {
      return { ok: false, error: "Session expired. Please login again." };
    }

    try {
      const submissions = await apiRequestWithToken("/api/submissions/my", user.token);
      return { ok: true, data: submissions };
    } catch (error) {
      if (
        error.message.includes("Session expired") ||
        error.message.includes("token failed") ||
        error.message.includes("No token provided")
      ) {
        logout();
      }
      return { ok: false, error: error.message };
    }
  };


  const createSubmission = async (formData) => {
    if (!user?.token) {
      return { ok: false, error: "Session expired. Please login again." };
    }

    try {
      const submission = await apiRequestFormWithToken(
        "/api/submissions",
        user.token,
        formData
      );
      return {
        ok: true,
        data: submission,
        message: "Submission saved successfully."
      };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const updateUserAvatar = (avatarDataUrl) => {
    if (!user?.email) {
      return;
    }

    const normalizedEmail = normalizeEmail(user.email);
    const nextMeta = {
      ...userMeta,
      [normalizedEmail]: {
        ...(userMeta[normalizedEmail] || {}),
        avatar: avatarDataUrl || ""
      }
    };

    persistMeta(nextMeta);

    const updatedSession = {
      ...user,
      avatar: avatarDataUrl || ""
    };
    setUser(updatedSession);
    localStorage.setItem(STORAGE_KEYS.userSession, JSON.stringify(updatedSession));
  };

  const updateUserName = async (name) => {
    if (!user?.token) {
      return { ok: false, error: "Session expired. Please login again." };
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      return { ok: false, error: "Name is required." };
    }

    try {
      const encodedName = encodeURIComponent(trimmedName);
      const payload = await apiRequestWithToken(
        `/api/users/update-name?name=${encodedName}`,
        user.token,
        {
          method: "PUT",
          body: JSON.stringify({ name: trimmedName })
        }
      );

      const updatedSession = {
        ...user,
        name: payload.name || trimmedName
      };
      setUser(updatedSession);
      localStorage.setItem(STORAGE_KEYS.userSession, JSON.stringify(updatedSession));

      return { ok: true, message: payload.message || "Name updated successfully." };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const requestEmailChangeOtp = async (newEmail) => {
    if (!user?.token) {
      return { ok: false, error: "Session expired. Please login again." };
    }

    if (!newEmail || !newEmail.trim()) {
      return { ok: false, error: "New email is required." };
    }

    try {
      const normalizedNewEmail = normalizeEmail(newEmail);
      const payload = await apiRequestWithToken(
        `/api/users/change-email-otp?email=${encodeURIComponent(normalizedNewEmail)}`,
        user.token,
        {
          method: "POST",
          body: JSON.stringify({ email: normalizedNewEmail })
        }
      );

      return { ok: true, message: payload.message || "OTP sent to new email." };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const verifyEmailChangeOtp = async ({ otp }) => {
    if (!user?.token) {
      return { ok: false, error: "Session expired. Please login again." };
    }

    try {
      const payload = await apiRequestWithToken(
        "/api/users/verify-change-email",
        user.token,
        {
          method: "POST",
          body: JSON.stringify({ otp: otp.trim() })
        }
      );
      const profile = await apiRequestWithToken("/api/users/profile", user.token);
      const normalizedNewEmail = normalizeEmail(profile.email || user.email);

      const oldKey = normalizeEmail(user.email);
      const newKey = normalizedNewEmail;
      const oldMeta = userMeta[oldKey] || {};
      const nextMeta = { ...userMeta };
      delete nextMeta[oldKey];
      nextMeta[newKey] = {
        ...(nextMeta[newKey] || {}),
        ...oldMeta
      };
      persistMeta(nextMeta);

      const updatedSession = {
        ...user,
        email: normalizedNewEmail
      };
      setUser(updatedSession);
      localStorage.setItem(STORAGE_KEYS.userSession, JSON.stringify(updatedSession));

      return { ok: true, message: payload.message || "Email updated successfully." };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  };

  const updateUserEmail = async () =>
    ({ ok: false, error: "Use OTP verification flow to update email." });

  const logout = () => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEYS.userSession);
  };

  const adminLogout = () => {
    setAdmin(null);
    localStorage.removeItem(STORAGE_KEYS.adminSession);
  };

  const value = {
    user,
    admin,
    getLoginOptions,
    sendLoginVerificationLink,
    verifyLoginEmailToken,
    requestLoginOtp,
    loginWithOtp,
    registerUser,
    verifySignupOtp,
    resendSignupOtp,
    login,
    logout,
    requestPasswordResetOtp,
    resetPassword,
    adminLogin,
    adminLogout,
    fetchAdminSubmissions,
    fetchMySubmissions,
    createSubmission,
    updateUserAvatar,
    updateUserName,
    requestEmailChangeOtp,
    verifyEmailChangeOtp,
    updateUserEmail
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
